import React, { useCallback, useRef, useState } from 'react';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import imageCompression from 'browser-image-compression';
import { colors, fonts, radius } from './theme';
import { storageErrorCopy } from '../lib/storageErrorCopy';

/**
 * Reusable file upload component with compression, HEIC conversion, optional crop, and progress.
 *
 * Props:
 *   storagePrefix - Firebase Storage path prefix (e.g., 'avatars/uid123')
 *   maxSizeMB     - Max file size after compression (default 1)
 *   outputFormat  - 'webp' | 'jpeg' | 'original' (default 'webp')
 *   aspectRatio   - Optional crop ratio (e.g., 1 for square, 16/9 for landscape). If omitted, no crop.
 *   multiple      - Allow multi-file select (default false)
 *   maxFiles      - Max files when multiple (default 20)
 *   onUploadComplete(url) - Called with download URL on single upload success
 *   onMultiUploadComplete(urls) - Called with array of URLs on multi upload success
 *   existingUrl   - Show this as the current image
 *   label         - Button/area label (default 'Upload photo')
 *   compact       - Smaller UI variant (default false)
 */
export default function FileUpload({
  storagePrefix = 'uploads',
  maxSizeMB = 1,
  outputFormat = 'webp',
  aspectRatio,
  multiple = false,
  maxFiles = 20,
  onUploadComplete,
  onMultiUploadComplete,
  existingUrl,
  label = 'Upload photo',
  compact = false,
}) {
  const [preview, setPreview] = useState(existingUrl || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [multiProgress, setMultiProgress] = useState(null); // { done, total }
  const fileRef = useRef(null);

  // Canvas crop
  const cropToAspectRatio = useCallback((file, ratio) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let sw, sh, sx, sy;
        const imgRatio = img.width / img.height;
        if (imgRatio > ratio) {
          // Image is wider — crop sides
          sh = img.height;
          sw = sh * ratio;
          sx = (img.width - sw) / 2;
          sy = 0;
        } else {
          // Image is taller — crop top/bottom
          sw = img.width;
          sh = sw / ratio;
          sx = 0;
          sy = (img.height - sh) / 2;
        }
        const maxDim = ratio >= 1 ? 1200 : 800;
        const ow = Math.min(sw, maxDim);
        const oh = ow / ratio;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(ow);
        canvas.height = Math.round(oh);
        canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        const mimeType = outputFormat === 'jpeg' ? 'image/jpeg' : 'image/webp';
        canvas.toBlob(
          (blob) => blob ? resolve(new File([blob], file.name, { type: mimeType })) : reject(new Error('Crop failed')),
          mimeType, 0.85,
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
      img.src = url;
    });
  }, [outputFormat]);

  // Process a single image file — HEIC convert + compress + optional crop
  const processImage = useCallback(async (file) => {
    let processed = file;

    // HEIC → target format
    if (/\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type)) {
      const heic2any = (await import('heic2any')).default;
      const toType = outputFormat === 'jpeg' ? 'image/jpeg' : 'image/webp';
      const blob = await heic2any({ blob: file, toType, quality: 0.85 });
      const ext = outputFormat === 'jpeg' ? '.jpg' : '.webp';
      processed = new File([blob], file.name.replace(/\.(heic|heif)$/i, ext), { type: toType });
    }

    // Compress + resize
    const targetType = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : undefined;
    processed = await imageCompression(processed, {
      maxSizeMB,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
      ...(targetType ? { fileType: targetType } : {}),
    });

    // Crop to aspect ratio if specified
    if (aspectRatio) {
      processed = await cropToAspectRatio(processed, aspectRatio);
    }

    return processed;
  }, [maxSizeMB, outputFormat, aspectRatio, cropToAspectRatio]);

  // Upload to Firebase Storage
  const uploadFile = useCallback(async (processed, fileName) => {
    const ts = Date.now();
    const ext = outputFormat === 'jpeg' ? '.jpg' : outputFormat === 'webp' ? '.webp' : fileName.split('.').pop();
    const safeName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/\.[^.]+$/, '');
    const path = `${storagePrefix}/${ts}_${safeName}.${ext}`;
    const sRef = storageRef(storage, path);

    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(sRef, processed);
      task.on('state_changed',
        (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        async () => {
          try { resolve(await getDownloadURL(task.snapshot.ref)); }
          catch (err) { reject(err); }
        },
      );
    });
  }, [storagePrefix, outputFormat]);

  // Handle single file
  const handleSingleFile = useCallback(async (file) => {
    setError('');
    setUploading(true);
    setProgress(0);

    try {
      // Preview immediately
      if (file.type.startsWith('image/') && !/heic|heif/i.test(file.type)) {
        setPreview(URL.createObjectURL(file));
      }

      const processed = await processImage(file);
      // Update preview after processing (HEIC converted)
      setPreview(URL.createObjectURL(processed));

      const url = await uploadFile(processed, file.name);
      setPreview(url);
      setUploading(false);
      setProgress(100);
      onUploadComplete?.(url);
    } catch (err) {
      setError(storageErrorCopy(err, 'Upload failed'));
      setUploading(false);
    }
  }, [processImage, uploadFile, onUploadComplete]);

  // Handle multiple files
  const handleMultipleFiles = useCallback(async (files) => {
    setError('');
    const selected = Array.from(files).slice(0, maxFiles);
    setMultiProgress({ done: 0, total: selected.length });
    setUploading(true);

    const urls = [];
    for (let i = 0; i < selected.length; i++) {
      try {
        const processed = await processImage(selected[i]);
        const url = await uploadFile(processed, selected[i].name);
        urls.push(url);
      } catch { /* skip failed */ }
      setMultiProgress({ done: i + 1, total: selected.length });
    }

    setUploading(false);
    setMultiProgress(null);
    onMultiUploadComplete?.(urls);
  }, [maxFiles, processImage, uploadFile, onMultiUploadComplete]);

  // Input change handler
  const handleChange = useCallback((e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (multiple) handleMultipleFiles(files);
    else handleSingleFile(files[0]);
    e.target.value = '';
  }, [multiple, handleSingleFile, handleMultipleFiles]);

  // Drop handler
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    if (multiple) handleMultipleFiles(files);
    else handleSingleFile(files[0]);
  }, [multiple, handleSingleFile, handleMultipleFiles]);

  const isCircle = aspectRatio === 1;
  const boxSize = compact ? 100 : 160;

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" multiple={multiple} style={{ display: 'none' }} onChange={handleChange} />

      {/* Single file UI */}
      {!multiple ? (
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          style={{
            width: boxSize, height: isCircle ? boxSize : boxSize * (aspectRatio ? 1 / aspectRatio : 0.75),
            borderRadius: isCircle ? '50%' : radius.md,
            border: preview ? 'none' : `2px dashed ${colors.pearl}`,
            overflow: 'hidden', cursor: uploading ? 'default' : 'pointer',
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: preview ? 'transparent' : '#f9f9f9',
          }}
        >
          {preview ? (
            <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
          ) : (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <span style={{ fontSize: compact ? 24 : 32, opacity: 0.3, display: 'block' }}>📷</span>
              <span style={{ fontSize: compact ? 10 : 12, color: colors.gray1, marginTop: 4, display: 'block' }}>{label}</span>
            </div>
          )}

          {/* Upload overlay */}
          {uploading ? (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              borderRadius: isCircle ? '50%' : radius.md,
            }}>
              <svg width="36" height="36" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                <circle cx="18" cy="18" r="15" fill="none" stroke={colors.coral} strokeWidth="3"
                  strokeDasharray={`${progress * 0.94} 94`} strokeLinecap="round" transform="rotate(-90 18 18)" />
              </svg>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, marginTop: 4 }}>
                {progress < 100 ? `${progress}%` : 'Done'}
              </span>
            </div>
          ) : null}
        </div>
      ) : (
        /* Multi-file UI */
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          style={{
            padding: '20px 16px', border: `2px dashed ${colors.pearl}`, borderRadius: radius.md,
            textAlign: 'center', cursor: uploading ? 'default' : 'pointer', background: '#f9f9f9',
          }}
        >
          {uploading && multiProgress ? (
            <div>
              <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 500, color: colors.noir, fontFamily: fonts.body }}>
                Uploading {multiProgress.done} of {multiProgress.total}…
              </p>
              <div style={{ height: 6, background: colors.pearl, borderRadius: 3, overflow: 'hidden', maxWidth: 240, margin: '0 auto' }}>
                <div style={{ height: '100%', width: `${multiProgress.total > 0 ? (multiProgress.done / multiProgress.total) * 100 : 0}%`, background: colors.coral, borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
            </div>
          ) : (
            <div>
              <span style={{ fontSize: 28, opacity: 0.3 }}>📷</span>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: colors.gray1, fontFamily: fonts.body }}>{label}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: colors.gray1 }}>Drag & drop or click to browse (up to {maxFiles})</p>
            </div>
          )}
        </div>
      )}

      {/* Change button for single uploads */}
      {!multiple && preview && !uploading ? (
        <button type="button" onClick={() => fileRef.current?.click()}
          style={{ background: 'none', border: 'none', color: colors.coralInk, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body, padding: '6px 0', display: 'block' }}>
          Change photo
        </button>
      ) : null}

      {/* Error */}
      {error ? <p style={{ margin: '6px 0 0', fontSize: 12, color: colors.errorRed, fontFamily: fonts.body }}>{error}</p> : null}
    </div>
  );
}
